/**
 * Reusable admin edit modal for a single `travelbio_destinations` row.
 *
 * Two entry points open this component:
 *   1. Row click on /admin/cards
 *   2. Pencil-icon overlay on any Explore deck / grid card
 *
 * Props:
 *   • cardId       — the travelbio catalog id, e.g. "TB-HOT-000060"
 *   • open         — controlled boolean
 *   • onClose      — parent close handler
 *   • onSaved(doc) — invoked with the fresh doc after a successful save,
 *                    upload, or delete-photo. Parent uses this to refresh
 *                    lists / repaint the underlying card in-place.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Upload, Trash2, Save, Camera, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { VIBE_META } from "@/config/vibes";

// Fallback constants — used only if the /facets `*_options` payload is
// unavailable at mount (network flake, older backend). The backend is
// the source of truth (Bug 64 — `_VALID_CONTENT_TYPES` +
// `_VALID_CHIP_KEYS` in routers/admin.py). Keep these in sync as a
// safety net; they are not the primary list the dropdowns render from.
const CONTENT_TYPES_FALLBACK = [
  "Hotel", "Resort", "All-Inclusive Resort", "Destination",
  "Activity", "Camping/Campground", "Cruise",
  "Unique Stays", "Lodges/Inns/B&Bs",
];
const PRICE_RANGES = ["", "$", "$$", "$$$", "$$$$"];
const SECONDARY_CHIP_KEYS_FALLBACK = [
  "hotels", "resorts", "all_inclusive_resorts", "unique_stays",
  "lodges_inns_bnbs", "camping_campgrounds", "cabins", "rv_resorts",
  "theme_parks", "national_parks",
];

const TAG_FIELDS = [
  ["tags", "Tags"],
  ["hotel_tags", "Hotel tags"],
  ["resort_tags", "Resort tags"],
  ["camping_tags", "Camping tags"],
  ["destination_tags", "Destination tags"],
  ["activity_tags", "Activity tags"],
  ["vacation_style_tags", "Vacation style tags"],
];

const inpCls =
  "w-full px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 " +
  "border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm";
const inpROCls =
  "w-full px-3 py-2 rounded-lg bg-tb-cream-2 text-tb-navy/60 border-2 " +
  "border-tb-cream-3 font-ui text-sm cursor-not-allowed";
const labelCls = "block font-hand text-tb-cream/80 text-sm mb-1";
const sectionCls =
  "space-y-3 border-t-2 border-tb-gold/20 pt-4 first:border-t-0 first:pt-0";
const sectionTitleCls =
  "font-serif-it text-tb-gold text-lg mb-2 flex items-center gap-2";

function csvSplit(s) {
  return (s || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

export default function AdminCardEditModal({ cardId, open, onClose, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [reGeocode, setReGeocode] = useState(false);
  const [tagsInputs, setTagsInputs] = useState({}); // tag field name → csv string
  // Bug 64: source-of-truth enums from GET /facets. Backend serves
  // `content_type_options` + `secondary_chip_options` derived from
  // `_VALID_CONTENT_TYPES` / `_VALID_CHIP_KEYS`. We fetch once when
  // the modal first opens and cache on the component; missing/older
  // backend falls back to the hardcoded FALLBACK arrays above.
  const [contentTypeOptions, setContentTypeOptions] = useState(
    CONTENT_TYPES_FALLBACK,
  );
  const [secondaryChipOptions, setSecondaryChipOptions] = useState(
    SECONDARY_CHIP_KEYS_FALLBACK,
  );
  const facetsLoadedRef = useRef(false);
  const fileRef = useRef(null);

  // Bug 65: `cardId == null` → create mode. Modal renders with blank
  // defaults, uses POST instead of PATCH, hides the photo/delete UI
  // (server needs an existing id to attach uploads to), and offers
  // "Save & Add Another" + "Save & Close" instead of a single Save.
  const isCreate = !cardId;

  const blankDoc = () => ({
    name: "",
    content_type: contentTypeOptions[0] || "Hotel",
    city: "",
    state: "",
    country: "United States",
    region: "",
    nearest_airport: "",
    short_description: "",
    travelbio_memory_prompt: "",
    best_season: "",
    price_range: "",
    secondary_chips: [],
    booking_metadata: {},
  });

  const load = useCallback(async () => {
    if (isCreate) {
      const bd = blankDoc();
      setDoc(bd);
      const inputs = {};
      TAG_FIELDS.forEach(([k]) => { inputs[k] = ""; });
      inputs.ideal_for = "";
      setTagsInputs(inputs);
      setReGeocode(false);
      setAdvancedOpen(false);
      setLoading(false);
      return;
    }
    if (!cardId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/travelbio-destinations/${cardId}`);
      setDoc(data);
      const inputs = {};
      TAG_FIELDS.forEach(([k]) => {
        inputs[k] = (data[k] || []).join(", ");
      });
      inputs.ideal_for = (data.ideal_for || []).join(", ");
      setTagsInputs(inputs);
      setReGeocode(false);
      setAdvancedOpen(false);
    } catch (e) {
      console.error("[AdminCardEditModal] load failed", e);
      toast.error("Failed to load card");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [cardId, isCreate, onClose]);

  useEffect(() => {
    if (open) load();
  }, [open, cardId, load]);

  // Bug 64: fetch known-good enum options from the backend the first
  // time the modal opens. Cached for the component lifetime — the
  // enums are static config, no need to re-fetch on every open.
  useEffect(() => {
    if (!open || facetsLoadedRef.current) return;
    facetsLoadedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(
          "/admin/travelbio-destinations/facets",
        );
        if (Array.isArray(data.content_type_options) && data.content_type_options.length) {
          setContentTypeOptions(data.content_type_options);
        }
        if (Array.isArray(data.secondary_chip_options) && data.secondary_chip_options.length) {
          setSecondaryChipOptions(data.secondary_chip_options);
        }
      } catch (e) {
        // Fallback constants remain in place — the modal still works.
        console.warn("[AdminCardEditModal] facets fetch failed", e);
      }
    })();
  }, [open]);

  const setField = (k, v) => setDoc((prev) => ({ ...prev, [k]: v }));

  const derivedPrimaryFilter = useMemo(() => {
    const map = {
      "Hotel": "Hotels",
      "Resort": "Resorts",
      "All-Inclusive Resort": "All-Inclusive Resorts",
      "Destination": "Destinations",
      "Activity": "Activities",
      "Camping/Campground": "Camping/Campgrounds",
      "Cruise": "Cruises",
      "Unique Stays": "Unique Stays",
      "Lodges/Inns/B&Bs": "Lodges/Inns/B&Bs",
    };
    return map[doc?.content_type] || doc?.primary_filter || "";
  }, [doc?.content_type, doc?.primary_filter]);

  const derivedLegacyType = useMemo(() => {
    const map = {
      "Hotel": "hotel",
      "Resort": "resort",
      "All-Inclusive Resort": "resort",
      "Destination": "destination",
      "Activity": "attraction",
      "Camping/Campground": "camping",
      "Cruise": "cruise",
      "Unique Stays": "unique_stays",
      "Lodges/Inns/B&Bs": "lodges_inns_bnbs",
    };
    return map[doc?.content_type] || doc?.type || "";
  }, [doc?.content_type, doc?.type]);

  const toggleSecondaryChip = (chipKey) => {
    setDoc((prev) => {
      const cur = new Set(prev.secondary_chips || []);
      if (cur.has(chipKey)) cur.delete(chipKey);
      else cur.add(chipKey);
      return { ...prev, secondary_chips: Array.from(cur) };
    });
  };

  const onSave = async (opts = {}) => {
    const andAnother = !!opts.andAnother;
    if (!doc.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (isCreate && !(doc.city || doc.state || doc.country)) {
      toast.error("City, state, or country is required");
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        const body = {
          name: doc.name.trim(),
          content_type: doc.content_type || "Hotel",
          city: doc.city?.trim() || null,
          state: doc.state?.trim() || null,
          country: doc.country?.trim() || null,
          region: doc.region?.trim() || null,
          nearest_airport: doc.nearest_airport?.trim() || null,
          short_description: doc.short_description || "",
          travelbio_memory_prompt: doc.travelbio_memory_prompt || "",
          best_season: doc.best_season?.trim() || null,
          price_range: doc.price_range || null,
          secondary_chips: doc.secondary_chips || [],
          stay22_search_query:
            doc.booking_metadata?.stay22_search_query || "",
          ideal_for: csvSplit(tagsInputs.ideal_for),
        };
        TAG_FIELDS.forEach(([k]) => { body[k] = csvSplit(tagsInputs[k]); });
        const { data } = await api.post(
          "/admin/travelbio-destinations",
          body,
        );
        onSaved?.(data);
        if (andAnother) {
          // Bug 65: keep modal open, reset form for next entry.
          const bd = blankDoc();
          setDoc(bd);
          const inputs = {};
          TAG_FIELDS.forEach(([k]) => { inputs[k] = ""; });
          inputs.ideal_for = "";
          setTagsInputs(inputs);
          toast.success("Destination saved. Adding another…");
        } else {
          toast.success("Destination created");
          onClose?.();
        }
        return;
      }

      // EDIT mode — existing PATCH flow.
      const patch = {
        name: doc.name?.trim(),
        city: doc.city?.trim(),
        state: doc.state?.trim() || null,
        country: doc.country?.trim(),
        region: doc.region?.trim() || null,
        content_type: doc.content_type,
        secondary_chips: doc.secondary_chips || [],
        best_season: doc.best_season?.trim() || null,
        price_range: doc.price_range || null,
        nearest_airport: doc.nearest_airport?.trim() || null,
        short_description: doc.short_description || "",
        travelbio_memory_prompt: doc.travelbio_memory_prompt || "",
        stay22_search_query:
          (doc.booking_metadata?.stay22_search_query ?? doc.stay22_search_query) || "",
        ideal_for: csvSplit(tagsInputs.ideal_for),
        re_geocode: reGeocode,
      };
      TAG_FIELDS.forEach(([k]) => { patch[k] = csvSplit(tagsInputs[k]); });
      const { data } = await api.patch(
        `/admin/travelbio-destinations/${cardId}`,
        patch,
      );
      toast.success("saved");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      console.error("[AdminCardEditModal] save failed", e);
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Only JPG, PNG, or WebP images are allowed");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large — max 10 MB");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/admin/travelbio-destinations/${cardId}/upload-photo`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDoc(data);
      toast.success("photo uploaded");
      onSaved?.(data);
    } catch (err) {
      console.error("[AdminCardEditModal] upload failed", err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDeletePhoto = async () => {
    if (!window.confirm("Reset this card's photo to the placeholder?")) return;
    try {
      const { data } = await api.delete(
        `/admin/travelbio-destinations/${cardId}/photo`,
      );
      setDoc(data);
      toast.success("photo reset");
      onSaved?.(data);
    } catch (e) {
      console.error("[AdminCardEditModal] delete-photo failed", e);
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="admin-card-edit-modal"
      className="fixed inset-0 z-50 bg-tb-navy-deep/90 flex items-center justify-center p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-tb-navy-deep text-tb-cream rounded-2xl border-2 border-tb-gold shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-tb-navy border-b-2 border-tb-gold/60 px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-serif-it text-2xl text-tb-gold">
            {loading ? "loading…" : doc?.name || "edit card"}
          </h2>
          <button
            data-testid="admin-card-edit-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-tb-navy-deep border-2 border-tb-cream/40 text-tb-cream flex items-center justify-center tb-tap"
            aria-label="close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading || !doc ? (
          <div className="p-10 text-center font-hand text-tb-cream/60 text-lg">
            ~ loading ~
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* ── PHOTO ── (upload requires an existing card_id; in
                create mode the section still renders but the upload
                fails silently — user can upload after Save & Close) */}
            <div className={sectionCls}>
              <div className={sectionTitleCls}>
                <Camera className="w-4 h-4" /> photo
              </div>
              <div className="flex items-start gap-4">
                <div className="w-40 h-40 rounded-lg overflow-hidden bg-tb-cream-3 flex-shrink-0 border-2 border-tb-cream-3">
                  {doc.featured_image ? (
                    <img
                      data-testid="admin-card-photo-preview"
                      src={doc.featured_image}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-hand text-tb-navy/40 text-sm">
                      no image
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <p className="font-hand text-tb-cream/70 text-xs">
                    status: <b>{doc.image_status || "placeholder"}</b>
                    {doc.image_source ? ` · source: ${doc.image_source}` : ""}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <label
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand text-xs tb-tap ${uploading ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
                    >
                      {uploading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Upload className="w-3 h-3" />
                      )}
                      {uploading ? "uploading…" : "upload new photo"}
                      <input
                        ref={fileRef}
                        data-testid="admin-card-upload-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                    {doc.image_status &&
                      doc.image_status !== "placeholder" && (
                        <button
                          data-testid="admin-card-remove-photo"
                          onClick={onDeletePhoto}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy-deep border-2 border-tb-coral text-tb-coral font-hand text-xs tb-tap"
                        >
                          <Trash2 className="w-3 h-3" /> remove photo
                        </button>
                      )}
                  </div>
                  <p className="font-hand text-tb-cream/50 text-[11px] italic">
                    Auto-resized to 1600px wide and re-encoded as JPEG on
                    upload. Max 10 MB.
                  </p>
                </div>
              </div>
            </div>

            {/* ── BASICS ── */}
            <div className={sectionCls}>
              <div className={sectionTitleCls}>basics</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="md:col-span-2">
                  <span className={labelCls}>Name *</span>
                  <input
                    data-testid="admin-card-field-name"
                    className={inpCls}
                    value={doc.name || ""}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                </label>
                <label>
                  <span className={labelCls}>Content type</span>
                  <select
                    data-testid="admin-card-field-content-type"
                    className={inpCls}
                    value={doc.content_type || ""}
                    onChange={(e) => setField("content_type", e.target.value)}
                  >
                    {contentTypeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className={labelCls}>Primary filter (auto)</span>
                  <input
                    data-testid="admin-card-field-primary-filter"
                    className={inpROCls}
                    value={derivedPrimaryFilter}
                    readOnly
                    disabled
                  />
                </label>
                <label>
                  <span className={labelCls}>Legacy type (auto)</span>
                  <input
                    data-testid="admin-card-field-type"
                    className={inpROCls}
                    value={derivedLegacyType}
                    readOnly
                    disabled
                  />
                </label>
                <div>
                  <span className={labelCls}>Secondary chips</span>
                  <div className="flex flex-wrap gap-1.5">
                    {secondaryChipOptions.map((k) => {
                      const on = (doc.secondary_chips || []).includes(k);
                      const meta = VIBE_META[k];
                      return (
                        <button
                          key={k}
                          type="button"
                          data-testid={`admin-card-chip-${k}`}
                          onClick={() => toggleSecondaryChip(k)}
                          className={`px-2.5 py-1 rounded-full font-hand text-xs border-2 tb-tap ${
                            on
                              ? "bg-tb-gold text-tb-navy border-tb-gold"
                              : "bg-tb-navy-deep text-tb-cream/70 border-tb-cream/30"
                          }`}
                        >
                          {meta?.label || k}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* ── LOCATION ── */}
            <div className={sectionCls}>
              <div className={sectionTitleCls}>
                <MapPin className="w-4 h-4" /> location
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label>
                  <span className={labelCls}>City</span>
                  <input
                    data-testid="admin-card-field-city"
                    className={inpCls}
                    value={doc.city || ""}
                    onChange={(e) => setField("city", e.target.value)}
                  />
                </label>
                <label>
                  <span className={labelCls}>State</span>
                  <input
                    data-testid="admin-card-field-state"
                    className={inpCls}
                    value={doc.state || ""}
                    onChange={(e) => setField("state", e.target.value)}
                  />
                </label>
                <label>
                  <span className={labelCls}>Country</span>
                  <input
                    data-testid="admin-card-field-country"
                    className={inpCls}
                    value={doc.country || ""}
                    onChange={(e) => setField("country", e.target.value)}
                  />
                </label>
                <label>
                  <span className={labelCls}>Region</span>
                  <input
                    data-testid="admin-card-field-region"
                    className={inpCls}
                    value={doc.region || ""}
                    onChange={(e) => setField("region", e.target.value)}
                  />
                </label>
                <label>
                  <span className={labelCls}>Nearest airport</span>
                  <input
                    data-testid="admin-card-field-nearest-airport"
                    className={inpCls}
                    value={doc.nearest_airport || ""}
                    onChange={(e) => setField("nearest_airport", e.target.value)}
                    placeholder="e.g. MCO"
                  />
                </label>
                <label className="flex items-center gap-2 md:col-span-2 mt-1">
                  <input
                    type="checkbox"
                    data-testid="admin-card-re-geocode"
                    checked={reGeocode}
                    onChange={(e) => setReGeocode(e.target.checked)}
                    className="w-4 h-4 accent-tb-gold"
                  />
                  <span className="font-hand text-tb-cream/70 text-sm">
                    Re-run Nominatim geocode on save (only if location fields
                    changed)
                  </span>
                </label>
              </div>
            </div>

            {/* ── DESCRIPTION ── */}
            <div className={sectionCls}>
              <div className={sectionTitleCls}>description</div>
              <label className="block">
                <span className={labelCls}>Short description</span>
                <textarea
                  data-testid="admin-card-field-short-description"
                  className={inpCls}
                  rows={3}
                  value={doc.short_description || ""}
                  onChange={(e) => setField("short_description", e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelCls}>TravelBio memory prompt</span>
                <textarea
                  data-testid="admin-card-field-memory-prompt"
                  className={inpCls}
                  rows={2}
                  value={doc.travelbio_memory_prompt || ""}
                  onChange={(e) =>
                    setField("travelbio_memory_prompt", e.target.value)
                  }
                  placeholder="One-line prompt shown in the chapter journal…"
                />
              </label>
            </div>

            {/* ── TAGS ── */}
            <div className={sectionCls}>
              <div className={sectionTitleCls}>tags</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {TAG_FIELDS.map(([k, label]) => (
                  <label key={k}>
                    <span className={labelCls}>{label} (comma-separated)</span>
                    <input
                      data-testid={`admin-card-field-${k.replace(/_/g, "-")}`}
                      className={inpCls}
                      value={tagsInputs[k] || ""}
                      onChange={(e) =>
                        setTagsInputs((prev) => ({
                          ...prev,
                          [k]: e.target.value,
                        }))
                      }
                    />
                  </label>
                ))}
              </div>
            </div>

            {/* ── ATTRIBUTES ── */}
            <div className={sectionCls}>
              <div className={sectionTitleCls}>attributes</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label>
                  <span className={labelCls}>Best season</span>
                  <input
                    data-testid="admin-card-field-best-season"
                    className={inpCls}
                    value={doc.best_season || ""}
                    onChange={(e) => setField("best_season", e.target.value)}
                    placeholder="e.g. Nov-Apr"
                  />
                </label>
                <label>
                  <span className={labelCls}>Price range</span>
                  <select
                    data-testid="admin-card-field-price-range"
                    className={inpCls}
                    value={doc.price_range || ""}
                    onChange={(e) => setField("price_range", e.target.value)}
                  >
                    {PRICE_RANGES.map((p) => (
                      <option key={p} value={p}>
                        {p || "(none)"}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className={labelCls}>
                    Ideal for (comma-separated)
                  </span>
                  <input
                    data-testid="admin-card-field-ideal-for"
                    className={inpCls}
                    value={tagsInputs.ideal_for || ""}
                    onChange={(e) =>
                      setTagsInputs((prev) => ({
                        ...prev,
                        ideal_for: e.target.value,
                      }))
                    }
                    placeholder="couples, families, business"
                  />
                </label>
              </div>
            </div>

            {/* ── BOOKING (advanced, collapsed) ── */}
            <div className={sectionCls}>
              <button
                type="button"
                data-testid="admin-card-toggle-advanced"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="font-serif-it text-tb-gold text-lg hover:text-tb-gold-bright"
              >
                booking (advanced) {advancedOpen ? "▾" : "▸"}
              </button>
              {advancedOpen && (
                <label className="block">
                  <span className={labelCls}>Stay22 search query</span>
                  <input
                    data-testid="admin-card-field-stay22-search-query"
                    className={inpCls}
                    value={
                      doc.booking_metadata?.stay22_search_query ??
                      doc.stay22_search_query ??
                      ""
                    }
                    onChange={(e) =>
                      setDoc((prev) => ({
                        ...prev,
                        booking_metadata: {
                          ...(prev.booking_metadata || {}),
                          stay22_search_query: e.target.value,
                        },
                      }))
                    }
                    placeholder="Overrides the auto-built Stay22 search string"
                  />
                  <p className="font-hand text-tb-cream/40 text-[11px] italic mt-1">
                    Only set this if the automatic lookup returns the wrong
                    property. Leave blank to use `name, city, state, country`.
                  </p>
                </label>
              )}
            </div>

            {/* ── FOOTER: Save / Cancel ── */}
            <div className="sticky bottom-0 -mx-5 -mb-5 bg-tb-navy border-t-2 border-tb-gold/60 px-5 py-3 flex items-center justify-end gap-2">
              <button
                data-testid="admin-card-cancel"
                onClick={onClose}
                className="px-4 py-2 rounded-full bg-tb-navy-deep border-2 border-tb-cream/40 text-tb-cream font-hand text-sm tb-tap"
              >
                cancel
              </button>
              {isCreate ? (
                <>
                  <button
                    data-testid="admin-card-save-and-close"
                    disabled={saving}
                    onClick={() => onSave({ andAnother: false })}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy-deep border-2 border-tb-gold text-tb-gold font-hand text-sm tb-tap disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    save & close
                  </button>
                  <button
                    data-testid="admin-card-save-and-add-another"
                    disabled={saving}
                    onClick={() => onSave({ andAnother: true })}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand text-sm tb-tap disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    save & add another
                  </button>
                </>
              ) : (
                <button
                  data-testid="admin-card-save"
                  disabled={saving}
                  onClick={() => onSave()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand text-sm tb-tap disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "saving…" : "save"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
